import { useContext, useEffect, useState } from "react";
import clsx from "clsx";
import { AbilityContext } from "@/shared/lib/casl";
import { useChatContext, useTrackActiveSection } from "../../model";
import { AiChatActions } from "../ai-chat-actions";
import { AiChatContent } from "../ai-chat-content";
import { AiChatHeader } from "../ai-chat-header";
import "./ai-chat.scss";

interface IAiChat {
  isOpen?: boolean;
  onToggle?: () => void;
}

const AiChatBody = ({
  open,
  onToggle,
  isApproveOnly,
  canEdit,
  canApprove,
  canIntegration,
}: {
  open: boolean;
  onToggle: () => void;
  isApproveOnly: boolean;
  canEdit: boolean;
  canApprove: boolean;
  canIntegration: boolean;
}) => {
  const { messages, actions } = useChatContext({
    isApproveOnly,
    canEdit,
    canApprove,
    canIntegration,
    isChatPanelOpen: open,
  });

  const isRejectedView = messages.some((m) => m.statusIcon === "rejected");

  return (
    <div
      className={clsx("ai-chat-container", open ? "ai-chat-container--open" : "ai-chat-container--closed", isRejectedView && "ai-chat-container--wide")}
    >
      <AiChatHeader isOpen={open} onToggle={onToggle} />
      <div
        className={clsx("ai-chat-panel", open ? "ai-chat-panel--open" : "ai-chat-panel--closed")}
      >
        <AiChatContent messages={messages} />
        <AiChatActions actions={actions} />
      </div>
    </div>
  );
};

export const AiChat = ({ isOpen, onToggle }: IAiChat) => {
  useTrackActiveSection();

  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof isOpen === "boolean";
  const open = isControlled ? isOpen : internalOpen;
  const handleToggle = () => {
    onToggle?.();
    if (!isControlled) {
      setInternalOpen((prev) => !prev);
    }
  };

  const ability = useContext(AbilityContext);
  // CaslProvider хранит один и тот же инстанс ability, а ability.update() не меняет reference.
  // Поэтому форсим перерендер по событию обновления ability.
  const [, forceRerender] = useState(0);

  useEffect(() => {
    const handler = () => forceRerender((x) => x + 1);
    // В разных версиях casl события называются по-разному: update/updated.
    // Подпишемся на оба безопасно.
    (ability as any).on?.("update", handler);
    (ability as any).on?.("updated", handler);
    return () => {
      (ability as any).off?.("update", handler);
      (ability as any).off?.("updated", handler);
    };
  }, [ability]);

  const canEdit = ability.can("edit", "title_journals");
  const canApprove = ability.can("approve", "title_journals");
  const canIntegration = ability.can("integration", "title_journals");
  const isApproveOnly = Boolean(canApprove && !canEdit);

  return (
    <AiChatBody
      open={open}
      onToggle={handleToggle}
      isApproveOnly={isApproveOnly}
      canEdit={canEdit}
      canApprove={canApprove}
      canIntegration={canIntegration}
    />
  );
};

